import React, { useState } from 'react';
import Proptypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';
import { useHistory } from 'react-router-dom';

import useManufacturerList from '../../../_shared/hooks/vehicle/useManufacturerList';
import ReducerConstants from '../../../_shared/constants/reducers';
import renderLocale from '../../../_shared/helpers/renderLocale';
import UserService from '../../../services/UserService';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import SegmentConfig from '../../../_shared/constants/segmentConfig';
import useAnalytics from '../../../_shared/hooks/useAnalytics';
import TradeService from '../../../services/TradeService';
import { TR5TradeStates, TradeStateName } from '../../../_shared/types/trade';
import ROUTES from '../../Routes/constants';
import Alerts from './Alerts';
import Todos from './Todos';
import ScaleLoader from '../../../_shared/components/ScaleLoader';
import { COOKIES } from '../../../_shared/constants/cookies';
import { createCookie, getCookie } from '../../../_shared/utils/cookies';

import {
  StyledNotificationsDropdown,
  NotificationsPickerContainer,
  NotificationsPicker,
  LabelButton,
  ListContainer,
  StatusLabel
} from './Styles';

const propTypes = {
  alerts: Proptypes.arrayOf(Proptypes.objectOf(Proptypes.any)),
  isAlertsError: Proptypes.bool.isRequired,
  refetchAlerts: Proptypes.func.isRequired,
  todos: Proptypes.arrayOf(Proptypes.objectOf(Proptypes.any)),
  isTodosError: Proptypes.bool.isRequired,
  refetchTodos: Proptypes.func.isRequired,
  openPopOver: Proptypes.func.isRequired,
  visitedTodos: Proptypes.bool.isRequired,
  setVisitedTodos: Proptypes.func.isRequired,
  isOmp: Proptypes.bool
};

const defaultProps = {
  alerts: [],
  todos: [],
  isOmp: false
};

const NotificationsDropdown = ({
  alerts,
  isAlertsError,
  isAlertsFetching,
  refetchAlerts,
  todos,
  isTodosError,
  isTodosFetching,
  refetchTodos,
  openPopOver,
  visitedTodos,
  setVisitedTodos,
  isOmp
}) => {
  const intl = useIntl();
  const user = useSelector(
    state => state[ReducerConstants.AUTH]?.user,
    shallowEqual
  );
  const userLocale = renderLocale(user?.auth);
  const [alertsActive, setAlertsActive] = useState(true);
  const { data: manufacturers } = useManufacturerList();

  const userService = UserService.get();
  const tradeService = TradeService.get();
  const history = useHistory();
  const track = useAnalytics();

  const handleAlertLabelClick = e => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    refetchAlerts();
    track(SegmentConfig.names.viewAlertsList, {});
    setAlertsActive(true);
  };

  const handleTodoLabelClick = e => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    refetchTodos();
    track(SegmentConfig.names.viewTodoList, {});
    setAlertsActive(false);
    setVisitedTodos(true);
  };

  const getManufacturer = manufacturerId => {
    const manufacturer = manufacturers.find(({ id }) => id === manufacturerId);
    return manufacturer ? manufacturer.displayName : '';
  };

  const handleRedirect = async ({
    eventName,
    tradeId,
    dealerId,
    tradeState,
    type,
    referrer
  }) => {
    track(eventName, {
      item_selected: true,
      trade_state: TradeStateName[tradeState],
      trade_id: tradeId,
      type,
      user_type: userService.isSeller(user, dealerId)
        ? MixpanelConfig.userType.seller
        : MixpanelConfig.userType.buyer
    });

    if (eventName === MixpanelConfig.names.selectAlert) {
      // this tracks that the to-do list was visited before the user clicked an alert item
      if (visitedTodos) {
        track(MixpanelConfig.names.selectTodo, {
          item_selected: false
        });
        setVisitedTodos(false);
      }
    } else {
      // this tracks that the alert list was visited before the user clicked a to-do item
      track(MixpanelConfig.names.selectAlert, {
        item_selected: false
      });
    }

    // update location cookie before landing on VDP (mixpanel sourse)
    const pageLocation = `${getCookie(COOKIES.LOCATION)}?flag=to-do_alerts`;
    createCookie(COOKIES.LOCATION, pageLocation);

    const currentTradeState = await tradeService.getTradeState(tradeId);
    const redirectToTR4 = !TR5TradeStates.includes(
      TradeStateName[currentTradeState]
    );
    if (isOmp) {
      window.location.href = `${ROUTES.OMP_ENDPOINT}trade/${tradeId}?utm_source=${referrer}`;
    } else if (redirectToTR4) {
      window.location.href = `${ROUTES.TR4_ENDPOINT}trade/${tradeId}`;
    } else {
      openPopOver(isOpen => !isOpen);
      history.push(`${ROUTES.TRADE_URI}${tradeId}`);
    }
  };

  return (
    <StyledNotificationsDropdown>
      <NotificationsPickerContainer>
        <NotificationsPicker>
          <LabelButton
            data-testid="alerts-button"
            onClick={handleAlertLabelClick}
            selected={alertsActive}
          >
            {intl.formatMessage({ id: 'notifications.alerts' })}
          </LabelButton>
          <LabelButton
            data-testid="todos-button"
            onClick={handleTodoLabelClick}
            selected={!alertsActive}
          >
            {intl.formatMessage({ id: 'notifications.todos' })}
          </LabelButton>
        </NotificationsPicker>
      </NotificationsPickerContainer>
      <ListContainer>
        {isAlertsFetching || isTodosFetching ? (
          <StatusLabel>
            <ScaleLoader />
          </StatusLabel>
        ) : (
          <>
            {alertsActive && (
              <Alerts
                alerts={alerts}
                isAlertsError={isAlertsError}
                getManufacturer={getManufacturer}
                locale={userLocale}
                handleRedirect={handleRedirect}
              />
            )}
            {!alertsActive && (
              <Todos
                todos={todos}
                isTodosError={isTodosError}
                getManufacturer={getManufacturer}
                locale={userLocale}
                handleRedirect={handleRedirect}
              />
            )}
          </>
        )}
      </ListContainer>
    </StyledNotificationsDropdown>
  );
};

NotificationsDropdown.propTypes = propTypes;
NotificationsDropdown.defaultProps = defaultProps;

export default React.memo(NotificationsDropdown);
